import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Container, Grid, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import ShoppingCart from '../../components/ShoppingCart';
import { productAPI } from '../../services/api';

const PlaceOrderNew = ({ currentUser, onOrderSubmit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Product ID - should match database
  const PRODUCT_ID = 'cmg9t4du8000g12ovvowovslh';

  const [quantity, setQuantity] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  
  // Product data from API
  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(true);
  const [productError, setProductError] = useState(null);

  // Fetch product data from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setProductLoading(true);
        setProductError(null);
        const response = await productAPI.getProduct(PRODUCT_ID);
        
        if (response.success && response.data) {
          const productData = response.data;
          // Debug: Log the product data to see what we're receiving
          console.log('Product data from API:', productData);
          console.log('basePrice from API:', productData.basePrice, 'Type:', typeof productData.basePrice);
          
          // Transform API product data to component format
          const parsedBasePrice = Number(productData.basePrice) || 0;
          console.log('Parsed basePrice:', parsedBasePrice);
          
          setProduct({
            id: productData.id,
            name: productData.name,
            sku: productData.sku,
            productId: productData.id,
            basePrice: parsedBasePrice,
            description: productData.description || '',
            images: productData.images && productData.images.length > 0 
              ? productData.images 
              : ['/UnisynHub/Assets/equicenta-card.png'], // Fallback image
            volume: productData.volume || '',
            storageTemp: productData.storageTemp || '',
            shelfLife: productData.shelfLife || '',
            category: productData.category || '',
            organization: productData.organization || '',
            rating: 5, // Default rating
            reviewCount: 6, // Default review count
            sizing: productData.volume || '5 mL Vial',
            shipping: 'Cryopreserved, shipped Next-Day on dry ice.',
            features: [
              'Advanced umbilical cord tissue therapy',
              'Proven results for equine athletes',
              'Minimally invasive treatment protocol',
              'Enhanced healing and recovery'
            ],
            additionalInfo: productData.description || 'Advanced regenerative therapy designed specifically for equine athletes and performance horses.',
            productType: 'equicent',
            clientBenefits: [
              'Priority shipping included',
              'Dedicated account support',
              'Volume discount tiers available'
            ]
          });
        } else {
          throw new Error('Failed to fetch product data');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setProductError(error.message || 'Failed to load product information');
      } finally {
        setProductLoading(false);
      }
    };

    fetchProduct();
  }, []);

  const primaryColor = '#5c725c'; // equicent green

  const handleQuantityChange = (change) => {
    setQuantity(Math.max(1, quantity + change));
  };

  const handleAddToCart = () => {
    if (!product) return;

    const newItem = {
      name: product.name,
      price: product.basePrice || product.clientPrice || 0,
      quantity: quantity,
      image: product.images && product.images.length > 0 ? product.images[0] : '/UnisynHub/Assets/equicenta-card.png',
      sku: product.sku,
      productId: product.productId || product.id || product.sku // Use productId for backend
    };

    // Check if item already exists in cart
    const existingItemIndex = cartItems.findIndex(item => item.sku === product.sku);

    if (existingItemIndex >= 0) {
      // Update quantity if item exists
      const updatedCartItems = [...cartItems];
      updatedCartItems[existingItemIndex].quantity += quantity;
      setCartItems(updatedCartItems);
    } else {
      // Add new item
      setCartItems([...cartItems, newItem]);
    }

    // Open cart sidebar
    setCartOpen(true);

    // Reset quantity to 1
    setQuantity(1);
  };

  const handleUpdateCartQuantity = (itemIndex, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemIndex);
      return;
    }

    const updatedCartItems = [...cartItems];
    updatedCartItems[itemIndex].quantity = newQuantity;
    setCartItems(updatedCartItems);
  };

  const handleRemoveFromCart = (itemIndex) => {
    const updatedCartItems = cartItems.filter((_, index) => index !== itemIndex);
    setCartItems(updatedCartItems);
  };

  const handleCheckout = () => {
    // Navigate to checkout with cart items
    setCartOpen(false);
    navigate('/client/checkout', {
      state: { cartItems }
    });
  };

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 }, // this pushes *everything* down on mobile
        backgroundColor: '#f9fafb', // optional background for full page
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          fontFamily: 'Poppins, sans-serif',
        }}
      >

        {/* Top Cart Summary Bar */}
        {cartItems.length > 0 && (
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: primaryColor,
            color: 'white',
            py: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, md: 2 }
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 9 21.1 9 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5H5.21L4.27 3H1V2M17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 19 21.1 19 20 18.1 18 17 18Z" />
                </svg>
                <Typography sx={{
                  fontWeight: 600,
                  fontSize: { xs: '14px', md: '16px' }
                }}>
                  {cartItems.reduce((total, item) => total + item.quantity, 0)} item{cartItems.reduce((total, item) => total + item.quantity, 0) !== 1 ? 's' : ''} in cart
                </Typography>
              </Box>

              {/* Item details - hidden on mobile, shown on larger screens */}
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1
              }}>
                {cartItems.slice(0, 2).map((item, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '13px'
                  }}>
                    {item.name} × {item.quantity}
                  </Box>
                ))}
                {cartItems.length > 2 && (
                  <Typography sx={{ fontSize: '13px', opacity: 0.8 }}>
                    +{cartItems.length - 2} more
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' }
            }}>
              <Typography sx={{
                fontWeight: 600,
                fontSize: { xs: '16px', md: '18px' }
              }}>
                Total: ${cartItems.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
              </Typography>
              <Box sx={{
                display: 'flex',
                gap: { xs: 1, md: 2 },
                flexDirection: { xs: 'row', sm: 'row' }
              }}>
                <Button
                  variant="contained"
                  onClick={() => setCartOpen(true)}
                  sx={{
                    bgcolor: 'white',
                    color: primaryColor,
                    fontWeight: 600,
                    fontSize: { xs: '12px', md: '14px' },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: '20px',
                    minWidth: { xs: 'auto', md: 'auto' },
                    '&:hover': {
                      bgcolor: '#f8f9fa',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isMobile ? 'View' : 'View Cart'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCheckout}
                  sx={{
                    bgcolor: '#1a5a1a',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '12px', md: '14px' },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: '20px',
                    minWidth: { xs: 'auto', md: 'auto' },
                    '&:hover': {
                      bgcolor: '#0d4a0d',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Checkout
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Loading State */}
        {productLoading && (
          <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: primaryColor, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading product information...
            </Typography>
          </Container>
        )}

        {/* Error State */}
        {productError && !productLoading && (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {productError}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ bgcolor: primaryColor }}
            >
              Retry
            </Button>
          </Container>
        )}

        {/* Main Content - Only show if product is loaded */}
        {!productLoading && !productError && product && (
          <>
        {/* Main Product Section */}
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Product Images Section */}
            <Grid item xs={12} md={6} lg={5}>
              <Box sx={{
                display: 'flex',
                gap: { xs: 1, md: 2 },
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                {/* Thumbnail Navigation */}
                <Box sx={{
                  width: { xs: '100%', sm: '70px' },
                  display: 'flex',
                  flexDirection: { xs: 'row', sm: 'column' },
                  alignItems: 'center',
                  gap: 1,
                  order: { xs: 2, sm: 1 }
                }}>
                  {/* Up Arrow - Hidden on mobile */}
                  {!isMobile && (
                    <Box sx={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.7 }
                    }}>
                      <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
                        <path d="M1 5L6 1L11 5" stroke="#999" strokeWidth="1.5" />
                      </svg>
                    </Box>
                  )}

                  {/* Thumbnail Images */}
                  {product.images.map((image, index) => (
                    <Box
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        width: { xs: '60px', sm: '70px' },
                        height: { xs: '60px', sm: '80px' },
                        borderRadius: '2px',
                        border: selectedImageIndex === index ? `2px solid ${primaryColor}` : '1px solid #e6e6e6',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        flexShrink: 0,
                        '&:hover': { borderColor: primaryColor }
                      }}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  ))}

                  {/* Down Arrow - Hidden on mobile */}
                  {!isMobile && (
                    <Box sx={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.7 }
                    }}>
                      <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
                        <path d="M1 1L6 5L11 1" stroke="#999" strokeWidth="1.5" />
                      </svg>
                    </Box>
                  )}
                </Box>

                {/* Main Product Image */}
                <Box sx={{
                  width: { xs: '100%', sm: '460px' },
                  height: { xs: '300px', sm: '400px', md: '560px' },
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  order: { xs: 1, sm: 2 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e6e6e6'
                }}>
                  <img
                    src={product.images[selectedImageIndex]}
                    alt={product.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {/* Product Information & Order Section */}
            <Grid item xs={12} md={6} lg={7}>
              {/* Product Name & Rating */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: { xs: '24px', md: '32px' },
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  lineHeight: { xs: '30px', md: '38px' },
                  mb: 1.5
                }}>
                  {product.name}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, md: 1.5 },
                  mb: 2,
                  flexWrap: 'wrap'
                }}>
                  {/* Star Rating */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Box key={star} sx={{ width: { xs: 14, md: 16 }, height: { xs: 14, md: 16 }, mr: 0.3 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="#FFD700">
                          <path d="M8 1L9.5 5.5H14L10.5 8L12 12.5L8 9.5L4 12.5L5.5 8L2 5.5H6.5L8 1Z" />
                        </svg>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontFamily: 'Poppins',
                    color: '#666',
                    fontWeight: 400
                  }}>
                    {product.reviewCount} Review{product.reviewCount !== 1 ? 's' : ''}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, color: '#b3b3b3', fontWeight: 500 }}>•</Typography>
                  <Typography sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    fontFamily: 'Poppins',
                    color: '#333',
                    fontWeight: 500
                  }}>
                    SKU: {product.sku}
                  </Typography>
                </Box>
              </Box>

              {/* Price Display */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: { xs: '24px', md: '28px' },
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  color: primaryColor,
                  lineHeight: { xs: '30px', md: '34px' }
                }}>
                  ${(product?.basePrice || 0).toFixed(2)}
                </Typography>
              </Box>

              {/* Divider Line */}
              <Box sx={{
                width: '100%',
                height: '1px',
                bgcolor: '#e6e6e6',
                mb: 2.5
              }} />


              {/* Order Summary Box */}
              <Box sx={{
                p: { xs: 2, md: 3 },
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                mb: 3
              }}>
                <Typography sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  mb: 2
                }}>
                  Order Summary
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, color: '#666' }}>Unit Price:</Typography>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    ${(product?.basePrice || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, color: '#666' }}>Quantity:</Typography>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    {quantity} vial{quantity !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, color: '#666' }}>Subtotal:</Typography>
                  <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    ${((product?.basePrice || 0) * quantity).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{
                  borderTop: '1px solid #e5e5e5',
                  pt: 1,
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, fontWeight: 600, color: '#1a1a1a' }}>
                    Total:
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, fontWeight: 600, color: primaryColor }}>
                    ${((product?.basePrice || 0) * quantity).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Quantity & Checkout Section */}
              <Box sx={{
                width: '100%',
                height: { xs: 'auto', md: '80px' },
                padding: { xs: '12px 0', md: '16px 0' },
                backgroundColor: '#ffffff',
                borderTop: '1px solid #e5e5e5',
                borderBottom: '1px solid #e5e5e5',
                boxShadow: '0px 1px 0px 0px #e5e5e5, 0px -1px 0px 0px #e5e5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 1, md: 2 },
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                {/* Quantity Selector */}
                <Box sx={{
                  width: { xs: '120px', md: '140px' },
                  height: '48px',
                  padding: '8px',
                  backgroundColor: '#ffffff',
                  borderRadius: '24px',
                  border: '1px solid #e6e6e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Minus Button */}
                  <Box
                    onClick={() => handleQuantityChange(-1)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#f2f2f2',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#e5e5e5' }
                    }}
                  >
                    <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                      <path d="M1 1H9" stroke="#333" strokeWidth="1.5" />
                    </svg>
                  </Box>

                  {/* Quantity */}
                  <Typography sx={{
                    width: '60px',
                    textAlign: 'center',
                    fontSize: '16px',
                    fontFamily: 'Poppins',
                    fontWeight: '500',
                    color: '#1a1a1a'
                  }}>
                    {quantity}
                  </Typography>

                  {/* Plus Button */}
                  <Box
                    onClick={() => handleQuantityChange(1)}
                    sx={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#f2f2f2',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#e5e5e5' }
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1V9M1 5H9" stroke="#333" strokeWidth="1.5" />
                    </svg>
                  </Box>
                </Box>

                {/* Add to Cart Button */}
                <Box
                  onClick={handleAddToCart}
                  sx={{
                    flex: 1,
                    height: '48px',
                    padding: { xs: '12px 24px', md: '14px 32px' },
                    backgroundColor: primaryColor,
                    borderRadius: '24px',
                    color: 'white',
                    fontSize: { xs: '14px', md: '16px' },
                    fontFamily: 'Poppins',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    '&:hover': { opacity: 0.9 },
                    transition: 'all 0.2s ease',
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Add to Cart • ${((product?.basePrice || 0) * quantity).toFixed(2)}
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M5 7.5H10M7.5 5V10" stroke="white" strokeWidth="2" />
                  </svg>
                </Box>
              </Box>

              {/* Client Benefits */}
              <Box sx={{
                width: '100%',
                padding: { xs: '16px', md: '20px' },
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0',
                mb: 3
              }}>
                <Typography sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  color: '#166534',
                  mb: 2
                }}>
                  Your Client Benefits
                </Typography>
                {product.clientBenefits.map((benefit, index) => (
                  <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}>
                    <Box sx={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#22c55e',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 2.5L3 4.5L7 1" stroke="white" strokeWidth="1.5" />
                      </svg>
                    </Box>
                    <Typography sx={{
                      fontSize: { xs: '12px', md: '14px' },
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      color: '#166534',
                      lineHeight: { xs: '18px', md: '20px' }
                    }}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Product Information Tabs - Same as Detail Page */}
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{
            width: '100%',
            borderTop: '1px solid #e5e5e5',
            backgroundColor: '#fff',
            mt: 4
          }}>
            {/* Tab Navigation */}
            <Box sx={{
              width: '100%',
              height: { xs: 'auto', md: '56px' },
              bgcolor: 'white',
              borderBottom: '1px solid #e5e5e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: { xs: 'wrap', md: 'nowrap' }
            }}>
              <Box sx={{ display: 'flex' }}>
                {[
                  { label: 'Product Details', value: 0 },
                  { label: 'Clinical Information', value: 1 },
                  { label: 'Shipping & Handling', value: 2 }
                ].map((tab) => (
                  <Box
                    key={tab.value}
                    onClick={() => setSelectedTab(tab.value)}
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: { xs: 1.5, md: 2 },
                      cursor: 'pointer',
                      borderBottom: selectedTab === tab.value ? `2px solid ${primaryColor}` : 'none',
                      bgcolor: selectedTab === tab.value ? 'white' : 'transparent',
                      minWidth: { xs: 'auto', md: '120px' },
                      textAlign: 'center'
                    }}
                  >
                    <Typography sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      color: selectedTab === tab.value ? '#1a1a1a' : '#4d4d4d',
                      lineHeight: { xs: '20px', md: '24px' }
                    }}>
                      {tab.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Tab Content */}
            <Box sx={{
              maxWidth: '1200px',
              margin: { xs: '16px auto', md: '32px auto' },
              px: { xs: 2, md: 3 },
              display: 'flex',
              gap: { xs: 2, md: 4 },
              justifyContent: 'center'
            }}>
              {selectedTab === 0 && (
                <Box sx={{ width: { xs: '100%', md: '800px' } }}>
                  <Typography sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    lineHeight: { xs: '20px', md: '24px' },
                    mb: 3
                  }}>
                    {product.description}
                  </Typography>

                  {/* Features List */}
                  <Box sx={{ mb: 3 }}>
                    {product.features.map((feature, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5
                      }}>
                        <Box sx={{
                          width: '18px',
                          height: '18px',
                          backgroundColor: primaryColor,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 3.5L3.5 6L9 1" stroke="white" strokeWidth="1.5" />
                          </svg>
                        </Box>
                        <Typography sx={{
                          fontSize: '15px',
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          color: '#1a1a1a',
                          lineHeight: '22px'
                        }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {selectedTab === 1 && (
                <Box sx={{ width: '800px' }}>
                  <Typography sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    mb: 3,
                    fontFamily: 'Poppins'
                  }}>
                    Clinical Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Composition:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      Umbilical cord tissue with growth factors and cytokines
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Mechanism of Action:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      Anti-inflammatory and regenerative healing cascade
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Clinical Indications:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      Ligament injuries, joint inflammation, soft tissue damage
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedTab === 2 && (
                <Box sx={{ width: '800px' }}>
                  <Typography sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    mb: 3,
                    fontFamily: 'Poppins'
                  }}>
                    Shipping & Handling
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Shipping Method:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      {product.shipping}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Storage Requirements:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      Store at 2-8°C. Do not freeze. Protect from light.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Delivery Timeline:
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      Next-day delivery for orders placed before 2 PM EST
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Container>


        {/* Shopping Cart Sidebar */}
        <ShoppingCart
          isOpen={cartOpen}
          onClose={() => setCartOpen(false)}
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={handleCheckout}
        />
          </>
        )}
      </Box>
    </Box>
  );
};

export default PlaceOrderNew;
